import { bandasAPI } from '../services/api';
import {
  downloadBrandedListadoExcel,
  downloadBrandedListadoPdf,
  loadImageForPdfExport,
  blobToPdfImageAssetWithAlpha,
} from './exportListadoBranded';

const LIST_TITLE_GESTION = 'Listado de obras';
const LIST_TITLE_EXHIBICION = 'Repertorio';
const COLUMNS_GESTION = ['#', 'Título', 'Autor', 'Género', 'Estado', 'Páginas', 'Carpetas', 'Notas del director'];
const COLUMNS_EXHIBICION = ['Título', 'Autor'];

function formatObraEstado(estado) {
  if (estado === 'LISTO') return 'Listo';
  if (estado === 'BORRADOR') return 'Borrador';
  if (estado === 'PROCESANDO') return 'Procesando';
  return estado || '';
}

function formatObraCarpetas(obra) {
  return (obra.carpetasObra || [])
    .map((co) => co.carpeta?.nombre)
    .filter(Boolean)
    .join('; ');
}

/** @typedef {'alfabetico' | 'genero'} ObrasExportOrden */

const SIN_GENERO_LABEL = 'Sin género';

/**
 * @param {object[]} obras
 * @param {ObrasExportOrden} [orden]
 */
export function sortObrasForExport(obras, orden = 'alfabetico') {
  const list = [...(obras || [])];
  const byTitulo = (a, b) =>
    (a.titulo || '').localeCompare(b.titulo || '', 'es', { sensitivity: 'base' });

  if (orden === 'genero') {
    return list.sort((a, b) => {
      const ga = a.genero?.nombre || SIN_GENERO_LABEL;
      const gb = b.genero?.nombre || SIN_GENERO_LABEL;
      const cmp = ga.localeCompare(gb, 'es', { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
      return byTitulo(a, b);
    });
  }

  return list.sort(byTitulo);
}

/**
 * @param {object[]} obras
 * @param {ObrasExportOrden} [orden]
 */
export function obrasToGestionExportRows(obras, orden = 'alfabetico') {
  return sortObrasForExport(obras, orden).map((o, idx) => [
    idx + 1,
    o.titulo || '',
    o.autor || '',
    o.genero?.nombre || '',
    formatObraEstado(o.estado),
    o._count?.paginas ?? 0,
    formatObraCarpetas(o),
    o.notasDirector || '',
  ]);
}

/**
 * @param {object[]} obras
 * @param {ObrasExportOrden} [orden]
 */
export function obrasToExhibicionExportRows(obras, orden = 'alfabetico') {
  return sortObrasForExport(obras, orden).map((o) => [o.titulo || '', o.autor || '']);
}

const PDF_COLUMN_STYLES_GESTION = {
  0: { cellWidth: 8 },
  1: { cellWidth: 48 },
  2: { cellWidth: 36 },
  3: { cellWidth: 28 },
  4: { cellWidth: 18 },
  5: { cellWidth: 14 },
  6: { cellWidth: 45 },
  7: { cellWidth: 60 },
};

const PDF_COLUMN_STYLES_EXHIBICION = {
  0: { cellWidth: 108 },
  1: { cellWidth: 72 },
};

async function loadBandLogoAssetForPdf(bandLogoUrl) {
  try {
    const res = await bandasAPI.getMyAvatarFile();
    if (res?.status === 200 && res.data?.size > 0) {
      return await blobToPdfImageAssetWithAlpha(res.data);
    }
  } catch {
    // fallback a URL pública
  }
  if (bandLogoUrl) {
    return loadImageForPdfExport(bandLogoUrl);
  }
  return null;
}

const EXCEL_COL_WIDTHS = [
  { wch: 4 },
  { wch: 32 },
  { wch: 24 },
  { wch: 18 },
  { wch: 12 },
  { wch: 8 },
  { wch: 28 },
  { wch: 36 },
];

/**
 * @param {{ obras: object[], bandName?: string, subtitle?: string, filename?: string, orden?: ObrasExportOrden }} params
 */
export async function downloadObrasListadoPdfGestion({ obras, bandName, subtitle, filename, orden = 'alfabetico' }) {
  const rows = obrasToGestionExportRows(obras, orden);
  await downloadBrandedListadoPdf({
    listTitle: LIST_TITLE_GESTION,
    columns: COLUMNS_GESTION,
    rows,
    bandName,
    subtitle,
    filename,
    columnStyles: PDF_COLUMN_STYLES_GESTION,
    defaultFilenamePrefix: 'obras_gestion',
  });
}

/**
 * @param {{ obras: object[], bandName?: string, bandLogoUrl?: string, subtitle?: string, filename?: string, orden?: ObrasExportOrden }} params
 */
export async function downloadObrasListadoPdfExhibicion({
  obras,
  bandName,
  bandLogoUrl,
  subtitle,
  filename,
  orden = 'alfabetico',
}) {
  const rows = obrasToExhibicionExportRows(obras, orden);
  const bandLogoAsset = await loadBandLogoAssetForPdf(bandLogoUrl);
  await downloadBrandedListadoPdf({
    listTitle: LIST_TITLE_EXHIBICION,
    columns: COLUMNS_EXHIBICION,
    rows,
    bandName,
    subtitle,
    filename,
    bandLogoAsset,
    bandLogoInHeader: Boolean(bandLogoAsset),
    orientation: 'portrait',
    columnStyles: PDF_COLUMN_STYLES_EXHIBICION,
    tableStyles: { fontSize: 10, cellPadding: 3 },
    defaultFilenamePrefix: 'obras_repertorio',
  });
}

/** @deprecated Use downloadObrasListadoPdfGestion */
export async function downloadObrasListadoPdf(params) {
  return downloadObrasListadoPdfGestion(params);
}

/**
 * @param {{ obras: object[], bandName?: string, subtitle?: string, filename?: string }} params
 */
export async function downloadObrasListadoExcel({ obras, bandName, subtitle, filename }) {
  const rows = obrasToGestionExportRows(obras);
  return downloadBrandedListadoExcel({
    listTitle: LIST_TITLE_GESTION,
    columns: COLUMNS_GESTION,
    rows,
    bandName,
    subtitle,
    filename,
    sheetName: 'Obras',
    colWidths: EXCEL_COL_WIDTHS,
    defaultFilenamePrefix: 'obras',
  });
}
